/**
 * useExecutionRailCollapsed
 * ─────────────────────────
 * Shared collapse state for the Execution Live Stream rail.
 * Persists per-browser in localStorage so the choice survives navigation.
 *
 * Consumers:
 *  - ExecutionEventStreamRail (renders full rail vs. thin dock)
 *  - ExecutionDomainLanding (swaps xl:pr reservation so main content reclaims space)
 */
import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "koredor.executionRailCollapsed.v1";
const EVENT = "koredor:execution-rail-collapsed-changed";

function read(): boolean {
  // Default: collapsed. User must explicitly expand to see the rail.
  if (typeof window === "undefined") return true;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v === null) return true;
    return v === "1";
  } catch {
    return true;
  }
}

export function useExecutionRailCollapsed(): [boolean, (next: boolean) => void] {
  const [collapsed, setCollapsed] = useState<boolean>(() => read());

  useEffect(() => {
    const sync = () => setCollapsed(read());
    window.addEventListener(EVENT, sync);
    window.addEventListener("storage", sync);
    return () => {
      window.removeEventListener(EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const update = useCallback((next: boolean) => {
    try {
      window.localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* noop */
    }
    setCollapsed(next);
    window.dispatchEvent(new Event(EVENT));
  }, []);

  return [collapsed, update];
}